import { c as _c } from "react/compiler-runtime";
/**
 * useSpeechRecognition Hook
 *
 * Provides speech-to-text functionality using the Web Speech API.
 * Handles browser compatibility, permission requests, and real-time transcription.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
type UseSpeechRecognitionOptions = {
  onTranscript?: (text: string) => void;
  continuous?: boolean; // Continue listening after user stops speaking
  interimResults?: boolean; // Show interim results while speaking
  language?: string; // Language code (e.g., 'en-US')
};
export function useSpeechRecognition(t0) {
  const $ = _c(22);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onTranscript,
    continuous: t2,
    interimResults: t3,
    language: t4
  } = t1;
  const continuous = t2 === undefined ? true : t2;
  const interimResults = t3 === undefined ? true : t3;
  const language = t4 === undefined ? "en-US" : t4;
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  let t5;
  let t6;
  if ($[2] !== onTranscript) {
    t5 = () => {
      onTranscriptRef.current = onTranscript;
    };
    t6 = [onTranscript];
    $[2] = onTranscript;
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[5] !== continuous || $[6] !== interimResults || $[7] !== language) {
    t7 = () => {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        setIsSupported(true);
        const recognition = new SpeechRecognition();
        recognition.continuous = continuous;
        recognition.interimResults = interimResults;
        recognition.lang = language;
        recognition.onresult = event => {
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              const transcriptText = event.results[i][0].transcript.trim();
              if (transcriptText && onTranscriptRef.current) {
                setTranscript(transcriptText);
                onTranscriptRef.current(transcriptText);
              }
            }
          }
        };
        recognition.onerror = event_0 => {
          if (event_0.error === "no-speech" || event_0.error === "aborted") {
            return;
          }
          console.error("Speech recognition error:", event_0.error);
          let errorMessage;
          let shouldStopListening = true;
          bb47: switch (event_0.error) {
            case "audio-capture":
              {
                errorMessage = "No microphone found. Please check your device.";
                break bb47;
              }
            case "not-allowed":
              {
                errorMessage = "Microphone access denied. Please allow microphone access.";
                break bb47;
              }
            case "network":
              {
                errorMessage = "Network error. Please check your connection.";
                shouldStopListening = false;
                break bb47;
              }
            default:
              {
                errorMessage = `Speech recognition error: ${event_0.error}`;
                shouldStopListening = false;
              }
          }
          if (shouldStopListening) {
            setError(errorMessage);
            isListeningRef.current = false;
            setIsListening(false);
          }
        };
        recognition.onend = () => {
          if (isListeningRef.current) {
            ;
            try {
              recognition.start();
            } catch (t9) {
              const err = t9;
              console.error("Failed to restart recognition:", err);
              isListeningRef.current = false;
              setIsListening(false);
            }
          } else {
            setIsListening(false);
          }
        };
        recognitionRef.current = recognition;
      } else {
        setIsSupported(false);
        setError("Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.");
      }
      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.abort();
        }
      };
    };
    t8 = [continuous, interimResults, language];
    $[5] = continuous;
    $[6] = interimResults;
    $[7] = language;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] !== isSupported) {
    t9 = () => {
      if (!isSupported) {
        setError("Speech recognition is not supported in this browser.");
        return;
      }
      if (recognitionRef.current && !isListeningRef.current) {
        ;
        try {
          setError(null);
          setTranscript("");
          isListeningRef.current = true;
          setIsListening(true);
          recognitionRef.current.start();
        } catch (t10) {
          const err_0 = t10;
          console.error("Error starting speech recognition:", err_0);
          setError("Failed to start speech recognition. Please try again.");
          isListeningRef.current = false;
          setIsListening(false);
        }
      }
    };
    $[10] = isSupported;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const startListening = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      if (recognitionRef.current && isListeningRef.current) {
        ;
        try {
          isListeningRef.current = false;
          setIsListening(false);
          recognitionRef.current.stop();
        } catch (t11) {
          const err_1 = t11;
          console.error("Error stopping speech recognition:", err_1);
        }
      }
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const stopListening = t10;
  let t11;
  if ($[13] !== startListening) {
    t11 = () => {
      if (isListeningRef.current) {
        stopListening();
      } else {
        startListening();
      }
    };
    $[13] = startListening;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const toggleListening = t11;
  let t12;
  if ($[15] !== error || $[16] !== isListening || $[17] !== isSupported || $[18] !== startListening || $[19] !== toggleListening || $[20] !== transcript) {
    t12 = {
      isListening,
      isSupported,
      error,
      transcript,
      startListening,
      stopListening,
      toggleListening
    };
    $[15] = error;
    $[16] = isListening;
    $[17] = isSupported;
    $[18] = startListening;
    $[19] = toggleListening;
    $[20] = transcript;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}